/**
 * figureTrace — **그림 자신에게서** 그림 속 글자를 읽는다.
 *
 * 계약: docs/exam-builder-v2/BE-20260827-figure-editing-apis.md §9-4(2026-08-31 트레이스)
 *
 * ── 왜 서버 응답이 아니라 SVG 를 읽나 (2026-09-01 실측) ──
 * 계약 §9-4 는 「응답 `trace_texts:[{id,content,…}]`」라고 적었는데, dev 실호출에서 **그 키가
 * 어느 응답에도 없었다.** 문제 61654 body/0 으로 셋을 다 찔러 본 결과 —
 *   · `GET …/figures`            → items[0] 키 14개, trace_texts 없음
 *   · `GET …/figures/…/history`  → item 키 14개, trace_texts 없음
 *   · `PATCH … {trace_texts:{…}}` → 200 `{applied:["t2"], item, updated_at}`, item 에도 없음
 * 화면의 「그림 속 글자」 칸은 그 키 하나를 열쇠로 삼고 있었으므로 **한 번도 뜬 적이 없다.**
 * 그동안 오너는 같은 일을 AI 재요청으로 하려 했고(서버 이력 v9~v18 의 재요청 8건이 전부
 * 「숫자를 6명으로」였다) 재요청은 보관 원본 사진을 다시 훑으므로 글자가 늘 되돌아왔다.
 *
 * 그런데 **id 와 지금 글자는 이미 화면 안에 있다.** 서버가 준 그림(`item.src`) 자체가 그것을
 * 품고 있다 — 트레이스 SVG 는 머리에 `<metadata id="figtrace">` 로 `{"texts":[{id,content,…}]}`
 * 를 달고 온다(실측 61654: t1~t4). PATCH 로 글자를 바꾸면 그 metadata 도 함께 갱신된다.
 * 그러니 서버가 키를 실어 줄 때까지 기다릴 이유가 없다. 응답에 실려 오면 그쪽이 이기고
 * (figureApi.readTraceTexts), 안 실려 오면 그림에서 읽는다.
 *
 * ── 이 파일이 지키는 것 넷 ──
 * ① **`<metadata id="figtrace">` 가 유일한 표식이다.** `<text id=…>` 를 훑는 폴백을 두지 않는다 —
 *    계약 §9-1 이 일반 도판의 라벨도 `<text>` 로 조판한다고 했으므로, 그것을 세면 트레이스가
 *    아닌 그림에도 [글자 저장]이 뜨고 누르면 409 `not_traced` 로 **확정 실패**한다. 이 저장소의
 *    규칙이 「눌리면 반드시 실패하는 버튼을 내지 않는다」다(figureText.figureCapabilities).
 *    못 찾으면 빈 목록이다 — 모르는 것으로 버튼을 만들지 않는다.
 * ② **SVG 인 것만 푼다.** 은행 item 의 `src` 는 픽셀 그림이면 원본 사진이고(계약 §1-2 「항상 옴」),
 *    실측 `original_src` 가 1.09MB PNG 였다. mime 을 안 보면 그림 창을 열 때마다 그 크기를
 *    base64 로 풀어 던지는 헛일을 한다.
 * ③ **DOMParser 로 읽는다.** metadata 안의 JSON 은 XML 이스케이프돼 있다(실측: 원문
 *    `<대관람차 안전수칙>` 이 `&lt;대관람차 안전수칙&gt;` 로 저장). 문자열에서 잘라 JSON.parse
 *    하면 그 이스케이프가 값이 되어, 사람이 그 칸을 한 글자만 손대도 `&lt;…&gt;` 가 그대로
 *    서버로 나가 그림에 리터럴로 그려진다.
 * ④ **UTF-8 은 `decodeURIComponent(escape(atob(…)))` 로 푼다.** `TextDecoder` 가 이 저장소의
 *    검사 환경(CRA 5 · jsdom)에 **없다** — 쓰면 시험이 첫 줄에서 죽는다. 같은 함정을 이 저장소는
 *    이미 한 번 겪었고 같은 처방을 골라 두었다(src/editor/nodes/EquationNode.tsx 의
 *    「TextEncoder와 Buffer 대신 encodeURIComponent 사용」).
 */

/** 이 문자열이 SVG 그림인가 — data-URI 든 날 SVG 텍스트든. */
function isSvgSource(src) {
  if (typeof src !== 'string' || src === '') return false;
  const head = src.slice(0, 64).trim();
  return /^data:image\/svg\+xml/i.test(head) || head.startsWith('<svg') || head.startsWith('<?xml');
}

/**
 * data-URI 를 SVG 본문으로 푼다. base64·percent 두 인코딩을 다 받는다 —
 * 서버는 base64 로 주지만(실측) 계약이 인코딩을 못 박지 않았고, 우리 화면도 다른 자리에서
 * percent 인코딩 SVG 를 만든다.
 */
function toSvgText(src) {
  if (!isSvgSource(src)) return '';
  if (!/^data:/i.test(src)) return src;
  const comma = src.indexOf(',');
  if (comma < 0) return '';
  const body = src.slice(comma + 1);
  const isBase64 = /;base64/i.test(src.slice(0, comma));
  try {
    // ④ TextDecoder 를 쓰지 않는다 — jsdom 에 없다(머리주석).
    return isBase64 ? decodeURIComponent(escape(atob(body))) : decodeURIComponent(body);
  } catch (_) {
    return '';
  }
}

/**
 * 트레이스 그림의 글자 목록 — `[{ id, content }]`. 아니면 빈 배열.
 *
 * ⚠ **파싱 실패는 예외로 오지 않는다.** DOMParser 는 잘못된 XML 에 던지지 않고 `<parsererror>`
 * 하나가 든 문서를 돌려준다. try/catch 만 두면 그 경우에 조용히 이상한 것을 읽으므로 직접 본다.
 */
export function readTraceTextsFromSvg(src) {
  const text = toSvgText(src);
  if (!text) return [];
  if (typeof DOMParser === 'undefined') return [];
  let doc;
  try {
    doc = new DOMParser().parseFromString(text, 'image/svg+xml');
  } catch (_) {
    return [];
  }
  if (!doc || doc.getElementsByTagName('parsererror').length > 0) return [];
  const meta = doc.querySelector('metadata#figtrace');
  if (!meta) return []; // ① 표식이 없으면 트레이스가 아니다 — 폴백을 두지 않는다
  let parsed;
  try {
    parsed = JSON.parse(meta.textContent || '');
  } catch (_) {
    return [];
  }
  const list = Array.isArray(parsed?.texts) ? parsed.texts : [];
  return list
    .filter((t) => t && typeof t.id === 'string' && t.id !== '')
    .map((t) => ({ id: t.id, content: typeof t.content === 'string' ? t.content : '' }));
}

export default readTraceTextsFromSvg;
